// @warp-css;
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames } from '@chbphone55/classnames';
import { i18n } from '@lingui/core';
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { FormControlMixin } from '@open-wc/form-control';
import { reset } from '../styles.js';
import { styles } from './styles.js';
import '../icon/icon.js';
import { detectLocale } from '../i18n.js';
const prefixSuffixWrapper = 'absolute top-0 bottom-0 flex justify-center items-center focusable rounded-4 focus:[--w-outline-offset:-2px] bg-transparent ';
const ccSuffix = {
    wrapper: prefixSuffixWrapper + 'right-0',
    wrapperWithLabel: 'w-max pr-12',
    wrapperWithIcon: 'w-40',
    label: 'antialiased block relative cursor-default pb-0 font-bold text-xs s-text',
};
const ccPrefix = {
    wrapper: prefixSuffixWrapper + 'left-0',
    wrapperWithLabel: 'w-max pl-12',
    wrapperWithIcon: 'w-40',
    label: 'antialiased block relative cursor-default pb-0 font-bold text-xs s-text',
};
/**
 * This component is usually used in other components like form elements to show a prefix or suffix. See for example `w-textfield`.
 *
 * [See Storybook for usage examples](https://warp-ds.github.io/elements/?path=/docs/forms-affix--docs)
 */
class WarpAffix extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /**
         * @summary Accessible label for icon-button variants.
         * @description Used to override the default `aria-label`s for the internal button when `search` or `clear` is enabled.
         */
        this.ariaLabel = null;
        /**
         * Add this property to render a clickable Warp close icon.
         *
         * Set an `aria-label` that explains the action when using this.
         *
         * @summary Renders a clear icon button.
         * @description Shows a clickable close icon meant for reset/clear actions, typically in a suffix slot.
        */
        this.clear = false;
        /**
         * Add this property to render a clickable Warp search icon.
         *
         * @summary Renders a search icon button.
         * @description Shows a clickable search icon, typically in a prefix slot for search fields.
        */
        this.search = false;
        /**
         * @summary Text label shown as prefix/suffix content.
         * @description Displays plain text such as a currency or unit label (for example `kr` or `%`) instead of an icon button.
         */
        this.label = '';
        /**
         * @summary Icon name rendered as prefix/suffix content.
         * @description Displays a non-interactive `w-icon` in the affix area using the provided icon name (for example `Search` or `Close`) instead of text.
         */
        this.icon = null;
    }
    static { this.styles = [reset, styles]; }
    /** @internal */
    get _classBase() {
        return this.slot === 'suffix' ? ccSuffix : ccPrefix;
    }
    /** @internal */
    get _classes() {
        return classNames([
            this._classBase.wrapper,
            this.label ? this._classBase.wrapperWithLabel : this._classBase.wrapperWithIcon,
        ]);
    }
    /** @internal */
    resetContainingTextField(e) {
        const textfield = this.closest('w-textfield');
        if (textfield) {
            textfield.resetFormControl();
        }
        e.stopPropagation();
    }
    /** @internal */
    submitContainingForm(e) {
        const form = this.internals.form;
        if (form) {
            form.submit();
        }
        e.stopPropagation();
    }
    /** @internal */
    get _searchButton() {
        const label = this.ariaLabel ||
            i18n._({
                id: 'affix.aria.search',
                message: 'Search',
                comment: 'Aria label for the search button in affix',
            });
        return html `
      <button aria-label="${label}" class="${this._classes}" type="submit" @click="${this.submitContainingForm.bind(this)}">
        <w-icon name="Search" size="small" locale="${detectLocale()}" class="flex"></w-icon>
      </button>
    `;
    }
    /** @internal */
    get _clearButton() {
        const label = this.ariaLabel ||
            i18n._({
                id: 'affix.aria.clearInput',
                message: 'Clear input',
                comment: 'Aria label for the clear input button in affix',
            });
        return html `
      <button aria-label="${label}" class="${this._classes}" type="reset" @click="${this.resetContainingTextField.bind(this)}">
        <w-icon name="Close" size="small" locale="${detectLocale()}" class="flex"></w-icon>
      </button>
    `;
    }
    get _icon() {
        if (this.icon) {
            return html `
        <div class="${this._classes}">
          <w-icon name="${this.icon}" size="small" locale="${detectLocale()}" class="flex"></w-icon>
        </div>
      `;
        }
        return '';
    }
    /** @internal */
    get _text() {
        return html `
      <div class="${this._classes}">
        <span class="${this._classBase.label}">${this.label}</span>
      </div>
    `;
    }
    /** @internal */
    get _markup() {
        if (this.label) {
            return this._text;
        }
        if (this.icon) {
            return this._icon;
        }
        if (this.search) {
            return this._searchButton;
        }
        if (this.clear) {
            return this._clearButton;
        }
        return undefined;
    }
    render() {
        return html `${this._markup}`;
    }
}
__decorate([
    property({ attribute: 'aria-label', reflect: true, useDefault: true })
], WarpAffix.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: Boolean })
], WarpAffix.prototype, "clear", void 0);
__decorate([
    property({ type: Boolean })
], WarpAffix.prototype, "search", void 0);
__decorate([
    property({ reflect: true, useDefault: true })
], WarpAffix.prototype, "label", void 0);
__decorate([
    property({ reflect: true, useDefault: true })
], WarpAffix.prototype, "icon", void 0);
if (!customElements.get('w-affix')) {
    customElements.define('w-affix', WarpAffix);
}
export { WarpAffix };
//# sourceMappingURL=affix.js.map